import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../utils/apiClient';

export function useMessages(userId) {
    const [chatId, setChatId] = useState(0);
    const [user, setUser] = useState({});
    const [currentUserId, setCurrentUserId] = useState(0);
    const [messages, setMessages] = useState([]);
    const [restriction, setRestriction] = useState({ blocked: false, canMessage: true, canCall: true });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Подгрузка истории вверх
    const [hasMore, setHasMore] = useState(false);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const cursorRef = useRef(null);
    const loadingOlderRef = useRef(false);

    useEffect(() => {
        if (!userId) {
            setLoading(false);
            return;
        }

        cursorRef.current = null;

        const fetchMessages = async () => {
            try {
                const response = await apiFetch(`/api/messages/${userId}`, {
                    method: 'GET'
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error('Ошибка при получении сообщений');
                }

                if (data?.success) {
                    setMessages(data?.messages ?? []);
                    setUser(data?.user ?? {});
                    setChatId(data?.chatId ?? 0);
                    setCurrentUserId(data?.currentUserId ?? 0);
                    setRestriction(data?.restriction ?? { blocked: false, canMessage: true, canCall: true });
                    cursorRef.current = data?.nextCursor ?? null;
                    setHasMore(!!data?.nextCursor);
                }
                else {
                    throw new Error('Ошибка при получении сообщений');
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchMessages();
    }, [userId]);

    // Возвращает порцию более старых сообщений (хронологический порядок),
    // чтобы вызывающая страница сама добавила их в начало списка и
    // сохранила позицию прокрутки.
    const loadOlder = useCallback(async () => {
        if (loadingOlderRef.current || !cursorRef.current) return [];
        loadingOlderRef.current = true;
        setLoadingOlder(true);

        try {
            const response = await apiFetch(
                `/api/messages/${userId}?cursor=${encodeURIComponent(cursorRef.current)}`,
                { method: 'GET' }
            );

            const data = await response.json();
            if (!response.ok || !data?.success) throw new Error('Ошибка при получении сообщений');

            const older = data?.messages ?? [];
            cursorRef.current = data?.nextCursor ?? null;
            setHasMore(!!data?.nextCursor);
            return older;
        } catch (err) {
            setHasMore(false);
            return [];
        } finally {
            loadingOlderRef.current = false;
            setLoadingOlder(false);
        }
    }, [userId]);

    return { messages, user, chatId, currentUserId, restriction, loading, error, hasMore, loadingOlder, loadOlder };
}
